/**
 * Presentation Layer - useMedicalReservationsUpload Hook
 * 診療予約CSVアップロード画面のメインフック
 */

import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { addDays, format } from 'date-fns';

// Presentation Layer - Utils
import { filterClientData } from '../utils/clientDataFilter';

// Domain Layer
import type { CsvFile } from '../../../../domain/medicalReservation/CsvFile';
import { createCsvFile, isValidCsvFile } from '../../../../domain/medicalReservation/CsvFile';
import type { CsvData } from '../../../../domain/medicalReservation/CsvData';
import type { TimeRange } from '../../../../domain/medicalReservation/TimeRange';
import { createTimeRange } from '../../../../domain/medicalReservation/TimeRange';
import type { ProcessingResult } from '../../../../domain/medicalReservation/ProcessingResult';
import type { RegistrationResult } from '../../../../domain/medicalReservation/RegistrationResult';
import type { OutsideTimeExportResult } from '../../../../domain/medicalReservation/OutsideTimeExportResult';
import { getDefaultTimeRange } from '../../../../domain/medicalReservation/TimeRangePolicy';

// Application Layer
import type { CsvFileReader } from '../../../../application/medicalReservation/ports/CsvFileReader';
import type { MedicalReservationProcessor } from '../../../../application/medicalReservation/ports/MedicalReservationProcessor';
import type { MedicalReservationRepository } from '../../../../application/medicalReservation/ports/MedicalReservationRepository';
import type { SpreadsheetExporter } from '../../../../application/medicalReservation/ports/SpreadsheetExporter';
import { filterByTimeRange } from '../../../../application/medicalReservation/usecases/FilterByTimeRange';

// Infrastructure Layer (Factory functions)
import { createBrowserCsvFileReader } from '../../../../infrastructure/file/BrowserCsvFileReader';
import { createMedicalReservationProcessor } from '../../../../infrastructure/adapters/MedicalReservationProcessorImpl';
import { createMedicalReservationRepository } from '../../../../infrastructure/repositories/MedicalReservationRepositoryImpl';
import { createGoogleSheetsExporter } from '../../../../infrastructure/adapters/GoogleSheetsExporterImpl';

// Constants
import { CSV_TO_DISPLAY_MAPPING } from '../../../../constants/csvHeaders';

/**
 * CSVヘッダーを正規化する
 * バックエンドAPIが期待するヘッダー名に変換する
 */
const normalizeHeader = (header: string): string => {
  const trimmed = header.trim();
  return CSV_TO_DISPLAY_MAPPING[trimmed] || trimmed;
};

/**
 * アップロード状態の型定義
 */
export interface MedicalReservationsUploadState {
  // ファイル・データ関連
  csvFile: CsvFile | null;
  csvData: CsvData | null;
  timeRange: TimeRange;
  filteredData: CsvData | null;
  outsideTimeRangeData: CsvData | null;

  // 処理結果関連
  processingResult: ProcessingResult | null;
  registrationResult: RegistrationResult | null;
  exportResult: OutsideTimeExportResult | null;

  // UI状態
  isLoading: boolean;
  isProcessing: boolean;
  isRegistering: boolean;
  isExporting: boolean;

  // 翌日指定モード関連
  isNextDayMode: boolean;
  excludedClientCount: number;
  nextDayFilteredData: CsvData | null;

  // エラー関連
  errors: string[];
  duplicateErrors: string[];
}

/**
 * アップロードアクションの型定義
 */
export interface MedicalReservationsUploadActions {
  // ファイル操作
  handleFileSelect: (file: File) => Promise<void>;

  // 時間帯フィルタリング
  setTimeRange: (timeRange: TimeRange) => void;
  handleTimeRangeFilter: () => Promise<void>;

  // データ加工
  handleDataProcess: () => Promise<void>;

  // データ登録
  handleDataRegistration: () => Promise<void>;

  // エクスポート
  handleExportOutsideTimeRange: () => Promise<void>;

  // 翌日指定モード
  handleSetNextDay: () => void;
  handleNextDayExport: () => Promise<void>;

  // リセット・エラー処理
  resetAll: () => void;
  clearErrors: () => void;
}

/**
 * フックの戻り値型
 */
export type UseMedicalReservationsUploadReturn = [
  MedicalReservationsUploadState,
  MedicalReservationsUploadActions
];

/**
 * 依存性注入用のオプション
 */
export interface UseMedicalReservationsUploadOptions {
  fileReader?: CsvFileReader;
  processor?: MedicalReservationProcessor;
  repository?: MedicalReservationRepository;
  exporter?: SpreadsheetExporter;
}

/**
 * 診療予約CSVアップロードのメインフック
 */
export const useMedicalReservationsUpload = (
  options: UseMedicalReservationsUploadOptions = {}
): UseMedicalReservationsUploadReturn => {
  const navigate = useNavigate();

  // 依存性の初期化（DIまたはデフォルト実装）
  const fileReader = options.fileReader ?? createBrowserCsvFileReader();
  const processor = options.processor ?? createMedicalReservationProcessor();
  const repository = options.repository ?? createMedicalReservationRepository();
  const exporter = options.exporter ?? createGoogleSheetsExporter();

  // ファイル・データ状態
  const [csvFile, setCsvFile] = useState<CsvFile | null>(null);
  const [csvData, setCsvData] = useState<CsvData | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>(getDefaultTimeRange());
  const [filteredData, setFilteredData] = useState<CsvData | null>(null);
  const [outsideTimeRangeData, setOutsideTimeRangeData] = useState<CsvData | null>(null);

  // 処理結果状態
  const [processingResult, setProcessingResult] = useState<ProcessingResult | null>(null);
  const [registrationResult, setRegistrationResult] = useState<RegistrationResult | null>(null);
  const [exportResult, setExportResult] = useState<OutsideTimeExportResult | null>(null);

  // UI状態
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // エラー状態
  const [errors, setErrors] = useState<string[]>([]);
  const [duplicateErrors, setDuplicateErrors] = useState<string[]>([]);

  // 翌日指定モード状態
  const [isNextDayMode, setIsNextDayMode] = useState(false);
  const [excludedClientCount, setExcludedClientCount] = useState(0);
  const [nextDayFilteredData, setNextDayFilteredData] = useState<CsvData | null>(null);

  // プログラムによる時間帯変更フラグ（自動解除を防ぐため）
  const isProgrammaticChange = useRef(false);

  /**
   * ファイル選択処理（Task 33）
   */
  const handleFileSelect = useCallback(
    async (file: File) => {
      setIsLoading(true);
      setErrors([]);

      try {
        // CsvFileオブジェクトを作成
        const newCsvFile = createCsvFile(file);

        // バリデーション
        if (!isValidCsvFile(newCsvFile)) {
          setErrors(['無効なCSVファイルです。形式とサイズを確認してください。']);
          return;
        }

        setCsvFile(newCsvFile);

        // ファイル読み込み・パース
        const result = await fileReader.readAndParse(file);

        if (!result.ok) {
          setErrors([result.error.message]);
          return;
        }

        setCsvData(result.value);

        // 時間帯をデフォルトに設定
        const defaultTimeRange = getDefaultTimeRange();
        setTimeRange(defaultTimeRange);

        console.log('✅ CSVファイルの読み込み完了:', {
          ファイル名: file.name,
          ヘッダー数: result.value.headers.length,
          データ行数: result.value.totalRows,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'ファイル読み込みに失敗しました';
        setErrors([message]);
      } finally {
        setIsLoading(false);
      }
    },
    [fileReader]
  );

  /**
   * 時間帯フィルタリング処理（Task 34）
   * 旧実装に合わせて、フィルタリング後にprocess APIを呼び出す
   */
  const handleTimeRangeFilter = useCallback(async () => {
    if (!csvData) {
      setErrors(['CSVデータがありません']);
      return;
    }

    // 時間帯が不完全な場合はエラー
    if (!timeRange.startDate || !timeRange.startTime || !timeRange.endDate || !timeRange.endTime) {
      setErrors(['時間帯を完全に指定してください']);
      return;
    }

    setIsProcessing(true);
    setErrors([]);

    try {
      // 1. 時間帯でフィルタリング
      const filterResult = filterByTimeRange(csvData, timeRange);

      setFilteredData(filterResult.filteredData);
      setOutsideTimeRangeData(filterResult.outsideTimeRangeData);

      console.log('✅ 時間帯フィルタリング完了:', {
        時間内: filterResult.filteredCount,
        時間外: filterResult.outsideCount,
      });

      // フィルタリングされたデータがない場合
      if (filterResult.filteredCount === 0) {
        setErrors(['指定された時間範囲に該当するデータが見つかりませんでした']);
        return;
      }

      // 2. フィルタリング後のデータでprocess APIを呼び出す
      const dataToProcess = filterResult.filteredData;
      const result = await processor.process({
        headers: dataToProcess.headers,
        rows: dataToProcess.rows,
        timeRange,
        fileName: csvFile?.name ?? 'unknown.csv',
      });

      if (!result.ok) {
        setErrors([result.error.message]);
        return;
      }

      setProcessingResult(result.value);

      console.log('✅ データ加工完了:', {
        処理件数: result.value.summary.totalCount,
        成功件数: result.value.summary.successCount,
        エラー件数: result.value.summary.errorCount,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'フィルタリングに失敗しました';
      setErrors([message]);
    } finally {
      setIsProcessing(false);
    }
  }, [csvData, timeRange, csvFile, processor]);

  /**
   * データ加工処理（Task 35）
   */
  const handleDataProcess = useCallback(async () => {
    const dataToProcess = filteredData ?? csvData;

    if (!dataToProcess) {
      setErrors(['処理対象のデータがありません']);
      return;
    }

    setIsProcessing(true);
    setErrors([]);

    try {
      const result = await processor.process({
        headers: dataToProcess.headers,
        rows: dataToProcess.rows,
        timeRange,
        fileName: csvFile?.name ?? 'unknown.csv',
      });

      if (!result.ok) {
        setErrors([result.error.message]);
        return;
      }

      setProcessingResult(result.value);

      console.log('✅ データ加工完了:', {
        処理件数: result.value.summary.totalCount,
        成功件数: result.value.summary.successCount,
        エラー件数: result.value.summary.errorCount,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'データ加工に失敗しました';
      setErrors([message]);
    } finally {
      setIsProcessing(false);
    }
  }, [filteredData, csvData, timeRange, csvFile, processor]);

  /**
   * データ登録処理（Task 36）
   */
  const handleDataRegistration = useCallback(async () => {
    if (!processingResult || processingResult.processedData.length === 0) {
      setErrors(['登録可能なデータがありません']);
      return;
    }

    if (!csvData) {
      setErrors(['CSVデータがありません']);
      return;
    }

    setIsRegistering(true);
    setErrors([]);
    setDuplicateErrors([]);

    try {
      const result = await repository.register({
        processedData: processingResult.processedData,
        fileName: csvFile?.name ?? 'unknown.csv',
        headers: csvData.headers,
      });

      if (!result.ok) {
        // 重複エラーの場合
        if (result.error.type === 'DUPLICATE' && result.error.affectedRows) {
          setDuplicateErrors(result.error.affectedRows.map((row) => `行 ${row}`));
        }
        setErrors([result.error.message]);
        return;
      }

      setRegistrationResult(result.value);

      console.log('✅ データ登録完了:', {
        成功件数: result.value.details?.successCount,
        失敗件数: result.value.details?.failureCount,
      });

      // 登録成功時に遷移
      if (result.value.success) {
        // 少し待ってから遷移
        setTimeout(() => {
          navigate('/appointments/list');
        }, 2000);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'データ登録に失敗しました';
      setErrors([message]);
    } finally {
      setIsRegistering(false);
    }
  }, [processingResult, csvData, csvFile, repository, navigate]);

  /**
   * 時間外データエクスポート処理（Task 37）
   */
  const handleExportOutsideTimeRange = useCallback(async () => {
    if (!outsideTimeRangeData || outsideTimeRangeData.totalRows === 0) {
      console.log('ℹ️ 時間外データがないため、エクスポートをスキップします');
      return;
    }

    setIsExporting(true);
    setErrors([]);

    try {
      // CsvDataをRecord配列に変換
      // ヘッダーを正規化（APIが期待する形式に変換）
      const normalizedHeaders = outsideTimeRangeData.headers.map(normalizeHeader);
      const dataArray = outsideTimeRangeData.rows.map((row) => {
        const record: Record<string, string> = {};
        normalizedHeaders.forEach((header, index) => {
          record[header] = row[index] ?? '';
        });
        return record;
      });

      const result = await exporter.export({
        data: dataArray,
        headers: normalizedHeaders,
        metadata: {
          originalFilename: csvFile?.name,
          totalRecords: csvData?.totalRows,
          outsideRecords: outsideTimeRangeData.totalRows,
          timeRange: {
            start: `${timeRange.startDate} ${timeRange.startTime}:00`,
            end: `${timeRange.endDate} ${timeRange.endTime}:00`,
          },
        },
        debug: process.env.NODE_ENV === 'development',
      });

      if (!result.ok) {
        setErrors([result.error.message]);
        return;
      }

      setExportResult(result.value);

      console.log('✅ Google Sheetsエクスポート完了:', {
        シート名: result.value.sheetName,
        書き込み行数: result.value.rowsWritten,
      });

      // スプレッドシートを新しいタブで開く
      if (result.value.spreadsheetUrl) {
        window.open(result.value.spreadsheetUrl, '_blank');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'エクスポートに失敗しました';
      setErrors([message]);
    } finally {
      setIsExporting(false);
    }
  }, [outsideTimeRangeData, csvFile, csvData, timeRange, exporter]);

  /**
   * 翌日指定モード設定（Task 5）
   * 翌日の日付と全時間帯（00:00〜23:45）を設定し、
   * クライアントデータを除外する
   */
  const handleSetNextDay = useCallback(() => {
    if (!csvData) {
      setErrors(['CSVデータがありません']);
      return;
    }

    try {
      // 翌日の日付を計算
      const tomorrow = addDays(new Date(), 1);
      const tomorrowStr = format(tomorrow, 'yyyy-MM-dd');

      // プログラムによる変更フラグを立てる
      isProgrammaticChange.current = true;

      // 時間帯を翌日の全時間帯に設定
      const nextDayTimeRange = createTimeRange(
        tomorrowStr,
        '00:00',
        tomorrowStr,
        '23:45'
      );
      setTimeRange(nextDayTimeRange);

      // クライアントデータをフィルタリング
      const filterResult = filterClientData(csvData);
      setNextDayFilteredData(filterResult.filteredData);
      setExcludedClientCount(filterResult.excludedCount);

      // 翌日指定モードを有効化
      setIsNextDayMode(true);

      console.log('✅ 翌日指定モード設定完了:', {
        翌日: tomorrowStr,
        除外クライアント件数: filterResult.excludedCount,
        フィルタ後件数: filterResult.filteredData.totalRows,
      });

      // プログラムによる変更フラグを解除
      setTimeout(() => {
        isProgrammaticChange.current = false;
      }, 0);
    } catch (error) {
      const message = error instanceof Error ? error.message : '翌日指定に失敗しました';
      setErrors([message]);
    }
  }, [csvData]);

  /**
   * 翌日データエクスポート処理（Task 6）
   * クライアントデータを除外した翌日データをスプレッドシートに出力
   */
  const handleNextDayExport = useCallback(async () => {
    if (!nextDayFilteredData || nextDayFilteredData.totalRows === 0) {
      setErrors(['出力対象のデータがありません']);
      return;
    }

    setIsExporting(true);
    setErrors([]);

    const MAX_RETRIES = 3;
    const RETRY_INTERVAL = 1000;
    const SPREADSHEET_URL =
      'https://docs.google.com/spreadsheets/d/1A7qDgvxybU7gaNtXGF1LUUAi7vSRGsX0FoI6MvaVSzQ/edit';

    // ポップアップブロッカー対策: ユーザーアクション時に先にタブを開く
    const newWindow = window.open('about:blank', '_blank', 'noopener,noreferrer');

    try {
      // CsvDataをRecord配列に変換
      const normalizedHeaders = nextDayFilteredData.headers.map(normalizeHeader);
      const dataArray = nextDayFilteredData.rows.map((row) => {
        const record: Record<string, string> = {};
        normalizedHeaders.forEach((header, index) => {
          record[header] = row[index] ?? '';
        });
        return record;
      });

      // リトライロジック付きでエクスポート
      let lastError: Error | null = null;
      for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
        try {
          const result = await exporter.export({
            data: dataArray,
            headers: normalizedHeaders,
            metadata: {
              originalFilename: csvFile?.name,
              totalRecords: csvData?.totalRows,
              outsideRecords: nextDayFilteredData.totalRows,
              timeRange: {
                start: `${timeRange.startDate} ${timeRange.startTime}:00`,
                end: `${timeRange.endDate} ${timeRange.endTime}:00`,
              },
            },
            debug: process.env.NODE_ENV === 'development',
          });

          if (!result.ok) {
            throw new Error(result.error.message);
          }

          setExportResult(result.value);

          console.log('✅ 翌日データエクスポート完了:', {
            シート名: result.value.sheetName,
            書き込み行数: result.value.rowsWritten,
          });

          // エクスポート成功後、開いておいたタブにURLを設定
          if (newWindow) {
            newWindow.location.href = SPREADSHEET_URL;
          }
          return;
        } catch (error) {
          lastError = error instanceof Error ? error : new Error('エクスポートに失敗しました');
          if (attempt < MAX_RETRIES) {
            console.log(`⚠️ リトライ ${attempt}/${MAX_RETRIES}...`);
            await new Promise((r) => setTimeout(r, RETRY_INTERVAL));
          }
        }
      }

      // 全リトライ失敗時は開いたタブを閉じる
      if (newWindow) {
        newWindow.close();
      }
      setErrors([lastError?.message ?? 'エクスポートに失敗しました']);
    } catch (error) {
      // エラー時は開いたタブを閉じる
      if (newWindow) {
        newWindow.close();
      }
      const message = error instanceof Error ? error.message : 'エクスポートに失敗しました';
      setErrors([message]);
    } finally {
      setIsExporting(false);
    }
  }, [nextDayFilteredData, csvFile, csvData, timeRange, exporter, navigate]);

  /**
   * 全状態リセット（Task 38）
   */
  const resetAll = useCallback(() => {
    setCsvFile(null);
    setCsvData(null);
    setTimeRange(getDefaultTimeRange());
    setFilteredData(null);
    setOutsideTimeRangeData(null);
    setProcessingResult(null);
    setRegistrationResult(null);
    setExportResult(null);
    setIsLoading(false);
    setIsProcessing(false);
    setIsRegistering(false);
    setIsExporting(false);
    setErrors([]);
    setDuplicateErrors([]);
    // 翌日指定モードをリセット
    setIsNextDayMode(false);
    setExcludedClientCount(0);
    setNextDayFilteredData(null);
  }, []);

  /**
   * エラークリア（Task 38）
   */
  const clearErrors = useCallback(() => {
    setErrors([]);
    setDuplicateErrors([]);
  }, []);

  /**
   * 時間帯変更ラッパー（Task 7）
   * 手動で時間帯が変更された場合、翌日指定モードを解除する
   */
  const handleSetTimeRange = useCallback((newTimeRange: TimeRange) => {
    setTimeRange(newTimeRange);

    // プログラムによる変更でない場合（手動変更）は翌日指定モードを解除
    if (!isProgrammaticChange.current && isNextDayMode) {
      setIsNextDayMode(false);
      setExcludedClientCount(0);
      setNextDayFilteredData(null);
      console.log('ℹ️ 手動変更により翌日指定モードを解除しました');
    }
  }, [isNextDayMode]);

  // 状態オブジェクト
  const state: MedicalReservationsUploadState = {
    csvFile,
    csvData,
    timeRange,
    filteredData,
    outsideTimeRangeData,
    processingResult,
    registrationResult,
    exportResult,
    isLoading,
    isProcessing,
    isRegistering,
    isExporting,
    isNextDayMode,
    excludedClientCount,
    nextDayFilteredData,
    errors,
    duplicateErrors,
  };

  // アクションオブジェクト
  const actions: MedicalReservationsUploadActions = {
    handleFileSelect,
    setTimeRange: handleSetTimeRange,
    handleTimeRangeFilter,
    handleDataProcess,
    handleDataRegistration,
    handleExportOutsideTimeRange,
    handleSetNextDay,
    handleNextDayExport,
    resetAll,
    clearErrors,
  };

  return [state, actions];
};
